import { useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, toApiError, unwrap, type Schemas } from "../../api/client";
import { ErrorMessage, Loading } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { formatDateTime } from "../../lib/format";
import { routes } from "../../lib/routes";
import { useQuery } from "../../lib/useQuery";

type Campaign = Schemas["CampaignOut"];
type Report = Schemas["ReportOut"];
type ReportStatus = Report["status"];

const TABS: readonly { id: ReportStatus; label: MessageKey }[] = [
  { id: "pending", label: "fau.tabPending" },
  { id: "approved", label: "fau.tabApproved" },
  { id: "rejected", label: "fau.tabRejected" },
];

const STATUS_KEYS: Record<ReportStatus, MessageKey> = {
  pending: "fau.tabPending",
  approved: "fau.tabApproved",
  rejected: "fau.tabRejected",
};

function ReportItem({
  orgId,
  nr,
  report: r,
  campaign,
  onModerated,
  onConflict,
}: {
  orgId: string;
  nr: string;
  report: Report;
  campaign: Campaign | undefined;
  onModerated: (r: Report) => void;
  onConflict: () => void;
}) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const received = formatDateTime(r.created_at);

  async function moderate(decision: "approve" | "reject") {
    setBusy(true);
    setError(null);
    try {
      const updated = await unwrap(
        api.POST("/api/orgs/{org_id}/kommuner/{nummer}/fau/reports/{report_id}/moderate", {
          params: { path: { org_id: orgId, nummer: nr, report_id: r.id } },
          body: { decision },
        }),
      );
      onModerated(updated);
    } catch (err) {
      const apiError = toApiError(err);
      // Another moderator decided this report first: refresh instead of leaving live buttons.
      if (apiError.status === 409) onConflict();
      else setError(apiError);
    } finally {
      setBusy(false);
    }
  }

  // A "confirm" report can link to its finding when the campaign is for one school.
  const mapHref =
    r.kind === "confirm" && r.finding_key && campaign?.school_id
      ? routes.school(orgId, nr, campaign.school_id, r.finding_key)
      : null;

  return (
    <li className="card">
      <h3 id={`${id}-heading`}>
        {t(r.kind === "confirm" ? "fau.kindConfirm" : "fau.kindNew")}{" "}
        {r.status !== "pending" && <span className="badge">{t(STATUS_KEYS[r.status])}</span>}
      </h3>
      <p className="muted small">{t("fau.received", { date: received })}</p>
      <dl className="details">
        {campaign && (
          <>
            <dt>{t("fau.campaign")}</dt>
            <dd>{campaign.title}</dd>
          </>
        )}
        <dt>{t("fau.finding")}</dt>
        <dd>
          {r.kind === "confirm"
            ? (r.finding_title ?? t("common.dash"))
            : r.lat !== null && r.lon !== null
              ? t("fau.point", { lat: r.lat.toFixed(5), lon: r.lon.toFixed(5) })
              : t("common.dash")}
          {mapHref && (
            <>
              {" "}
              <Link to={mapHref}>{t("fau.showOnMap")}</Link>
            </>
          )}
        </dd>
        <dt>{t("fau.category")}</dt>
        <dd>{r.category_label}</dd>
        <dt>{t("fau.comment")}</dt>
        {/* Parent-written text: rendered as plain text only (React escapes it). */}
        <dd className="fau-comment">
          {r.comment ?? (r.status === "rejected" ? t("fau.commentDeleted") : t("fau.noComment"))}
        </dd>
      </dl>
      {r.status === "pending" && (
        <>
          <div className="link-group">
            <button
              type="button"
              className="btn btn-primary btn-small"
              disabled={busy}
              aria-label={t("fau.approveLabel", { date: received })}
              onClick={() => void moderate("approve")}
            >
              {t("fau.approve")}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-small"
              disabled={busy}
              aria-label={t("fau.rejectLabel", { date: received })}
              aria-describedby={`${id}-reject-hint`}
              onClick={() => void moderate("reject")}
            >
              {t("fau.reject")}
            </button>
          </div>
          <p id={`${id}-reject-hint`} className="hint">
            {t("fau.rejectHint")}
          </p>
        </>
      )}
      {error && <ErrorMessage error={error} />}
    </li>
  );
}

export function FauModeration({
  orgId,
  nr,
  campaigns,
  onModerated,
  onMessage,
}: {
  orgId: string;
  nr: string;
  campaigns: readonly Campaign[];
  onModerated: () => void;
  onMessage: (msg: string) => void;
}) {
  const id = useId();
  const [status, setStatus] = useState<ReportStatus>("pending");
  const [campaignId, setCampaignId] = useState("");
  const tabRefs = useRef<Partial<Record<ReportStatus, HTMLButtonElement | null>>>({});
  const reports = useQuery(`fau-reports:${orgId}:${nr}:${status}:${campaignId}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}/fau/reports", {
        params: {
          path: { org_id: orgId, nummer: nr },
          query: { status, ...(campaignId ? { campaign_id: campaignId } : {}) },
        },
        signal,
      }),
    ),
  );

  function select(tab: ReportStatus, focus = false) {
    setStatus(tab);
    if (focus) tabRefs.current[tab]?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = TABS.findIndex((x) => x.id === status);
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (i + 1) % TABS.length;
    if (e.key === "ArrowLeft") next = (i - 1 + TABS.length) % TABS.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = TABS.length - 1;
    const tab = next === null ? undefined : TABS[next];
    if (tab) {
      e.preventDefault();
      select(tab.id, true);
    }
  }

  function moderated(updated: Report) {
    // Keep the row in place with its new status so the moderator sees the result.
    reports.setData((prev) => prev?.map((r) => (r.id === updated.id ? updated : r)));
    onMessage(t(updated.status === "approved" ? "fau.approved" : "fau.rejected"));
    onModerated();
  }

  function conflict() {
    reports.reload();
    onMessage(t("fau.alreadyDecided"));
    onModerated();
  }

  return (
    <section className="section" aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`}>{t("fau.moderationTitle")}</h2>
      <p>{t("fau.moderationIntro")}</p>
      <div className="toolbar">
        <div className="field">
          <label htmlFor={`${id}-campaign`}>{t("fau.campaignFilter")}</label>
          <select
            id={`${id}-campaign`}
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">{t("fau.allCampaigns")}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="tabs" role="tablist" aria-label={t("fau.tabs")} onKeyDown={onKeyDown}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[tab.id] = el;
            }}
            type="button"
            role="tab"
            id={`${id}-tab-${tab.id}`}
            aria-selected={status === tab.id}
            aria-controls={`${id}-panel`}
            tabIndex={status === tab.id ? 0 : -1}
            className={status === tab.id ? "tab active" : "tab"}
            onClick={() => select(tab.id)}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${status}`}>
        {reports.error && <ErrorMessage error={reports.error} onRetry={reports.reload} />}
        {!reports.data && !reports.error && <Loading />}
        {reports.data?.length === 0 && <p>{t("fau.reportsEmpty")}</p>}
        {!!reports.data?.length && (
          <ul className="plain-list" aria-label={t("fau.reportsCaption")}>
            {reports.data.map((r) => (
              <ReportItem
                key={r.id}
                orgId={orgId}
                nr={nr}
                report={r}
                campaign={campaigns.find((c) => c.id === r.campaign_id)}
                onModerated={moderated}
                onConflict={conflict}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
